import React, { useState, useRef, useEffect, useMemo } from 'react';
import ColorField from './ColorField';
import ConfirmDialog from './ConfirmDialog';
import './MobilePromptBar.css';

// Icon mapping for each field type (same as desktop)
const FIELD_ICONS = {
    for: { normal: '/prompt_icon/for-icon.svg', hover: '/prompt_icon/for-icon-hover.svg' },
    color: { normal: '/prompt_icon/color-icon.svg', hover: '/prompt_icon/color-icon-hover.svg' },
    style: { normal: '/prompt_icon/style-icon.svg', hover: '/prompt_icon/style-icon-hover.svg' },
    typography: { normal: '/prompt_icon/font-icon.svg', hover: '/prompt_icon/font-icon-hover.svg' },
    size: { normal: '/prompt_icon/size-icon.svg', hover: '/prompt_icon/size-icon-hover.svg' },
    mood: { normal: '/prompt_icon/mood-icon.svg', hover: '/prompt_icon/mood-icon-hover.svg' },
    layout: { normal: '/prompt_icon/layout-icon.svg', hover: '/prompt_icon/layout-icon-hover.svg' },
    material: { normal: '/prompt_icon/material-icon.svg', hover: '/prompt_icon/material-icon-hover.svg' },
    dynamic_input: { normal: '/prompt_icon/dynamic-input-icon.svg', hover: '/prompt_icon/dynamic-input-icon-hover.svg' },
    assets: { normal: '/prompt_icon/asset-icon.svg', hover: '/prompt_icon/asset-icon-hover.svg' }
};

const MobilePromptBar = ({
    fields = [],
    formData = {},
    onRegenerate,
    assets = []
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeFieldId, setActiveFieldId] = useState(null);
    const [editedValues, setEditedValues] = useState({ ...formData, assets: assets });
    const [originalValues, setOriginalValues] = useState({ ...formData, assets: assets });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [assetPreviews, setAssetPreviews] = useState(() => {
        return (assets || []).map(asset => {
            if (typeof asset === 'string') return asset;
            if (asset instanceof File) return URL.createObjectURL(asset);
            return null;
        });
    });

    const fileInputRef = useRef(null);
    const panelRef = useRef(null);

    // Combine fields with asset field (memoized to prevent unnecessary re-renders)
    const allFields = useMemo(() => {
        const assetField = {
            id: 'assets',
            display: 'ASSET',
            type: 'file',
            placeholder: 'Upload reference images...'
        };
        return [...fields, assetField];
    }, [fields]);

    // Set initial active field when expanded
    useEffect(() => {
        if (isExpanded && !activeFieldId && allFields.length > 0) {
            setActiveFieldId(allFields[0].id);
        }
    }, [isExpanded, activeFieldId, allFields]);

    // Sync editedValues.assets and assetPreviews when assets prop changes
    useEffect(() => {
        setEditedValues(prev => ({ ...prev, assets: assets }));
        setOriginalValues(prev => ({ ...prev, assets: assets }));
        setAssetPreviews(
            (assets || []).map(asset => {
                if (typeof asset === 'string') return asset;
                if (asset instanceof File) return URL.createObjectURL(asset);
                return null;
            })
        );
    }, [assets]);

    // Sync formData changes
    useEffect(() => {
        setEditedValues(prev => ({ ...prev, ...formData }));
        setOriginalValues(prev => ({ ...prev, ...formData }));
    }, [formData]);

    // Get icon for a field
    const getIcon = (fieldId, isActive) => {
        const icons = FIELD_ICONS[fieldId] || FIELD_ICONS.dynamic_input;
        return isActive ? icons.hover : icons.normal;
    };

    // Check if field has been modified
    const hasFieldModified = (fieldId) => {
        if (fieldId === 'assets') {
            const original = originalValues.assets || [];
            const current = editedValues.assets || [];
            if (original.length !== current.length) return true;
            return false;
        }
        return editedValues[fieldId] !== originalValues[fieldId];
    };

    // Handle expand/collapse
    const handleBarClick = () => {
        if (!isExpanded) {
            setIsExpanded(true);
        }
    };

    const handleOverlayClick = () => {
        setIsExpanded(false);
        setActiveFieldId(null);
    };

    // Handle field selection
    const handleFieldSelect = (fieldId) => {
        setActiveFieldId(fieldId);
    };

    // Handle value change
    const handleValueChange = (e) => {
        const { name, value } = e.target;
        setEditedValues(prev => ({ ...prev, [name]: value }));
    };

    // Handle file upload for assets
    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            const newAssets = [...(editedValues.assets || []), ...files];
            setEditedValues(prev => ({ ...prev, assets: newAssets }));
            const newPreviews = files.map(file => URL.createObjectURL(file));
            setAssetPreviews(prev => [...prev, ...newPreviews]);
        }
    };

    // Remove an asset
    const handleRemoveAsset = (index) => {
        setEditedValues(prev => ({
            ...prev,
            assets: prev.assets.filter((_, i) => i !== index)
        }));
        setAssetPreviews(prev => prev.filter((_, i) => i !== index));
    };

    // Get preview URL for asset
    const getAssetPreviewUrl = (asset, index) => {
        if (assetPreviews[index]) return assetPreviews[index];
        if (typeof asset === 'string') return asset;
        if (asset instanceof File) return URL.createObjectURL(asset);
        return null;
    };

    // Handle confirm (save changes for current field)
    const handleConfirmField = () => {
        if (activeFieldId) {
            setOriginalValues(prev => ({
                ...prev,
                [activeFieldId]: editedValues[activeFieldId]
            }));
        }
    };

    // Handle submit - show confirm dialog
    const handleSubmit = () => {
        setShowConfirmDialog(true);
    };

    // Confirm generation
    const handleConfirmGenerate = () => {
        setShowConfirmDialog(false);
        if (onRegenerate) {
            onRegenerate(editedValues);
        }
        setIsExpanded(false);
        setActiveFieldId(null);
    };

    // Cancel generation
    const handleCancelGenerate = () => {
        setShowConfirmDialog(false);
    };

    // Get active field
    const activeField = allFields.find(f => f.id === activeFieldId);

    // Render content based on active field type
    const renderFieldContent = () => {
        if (!activeField) return null;

        if (activeField.id === 'assets') {
            return (
                <div className="mobile-prompt-assets-content">
                    <div className="mobile-prompt-assets-scroll">
                        {editedValues.assets && editedValues.assets.length > 0 && (
                            <div className="mobile-prompt-assets-grid">
                                {editedValues.assets.map((asset, index) => (
                                    <div key={index} className="mobile-prompt-asset-item">
                                        <img
                                            src={getAssetPreviewUrl(asset, index)}
                                            alt={`Asset ${index + 1}`}
                                            className="mobile-prompt-asset-img"
                                        />
                                        <button
                                            type="button"
                                            className="mobile-prompt-asset-remove"
                                            onClick={() => handleRemoveAsset(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className="mobile-prompt-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4L12 16M12 4L8 8M12 4L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            );
        }

        if (activeField.type === 'color') {
            return (
                <div className="mobile-prompt-color-content">
                    <ColorField
                        id={activeField.id}
                        display={activeField.display}
                        placeholder={activeField.placeholder}
                        value={editedValues[activeField.id] || ''}
                        onChange={handleValueChange}
                        placement="up"
                    />
                </div>
            );
        }

        if (activeField.type === 'smart_dropdown' || activeField.type === 'dropdown') {
            return (
                <div className="mobile-prompt-options-content">
                    {activeField.type === 'smart_dropdown' && (
                        <input
                            type="text"
                            name={activeField.id}
                            value={editedValues[activeField.id] || ''}
                            onChange={handleValueChange}
                            placeholder={activeField.placeholder || 'Or type your own...'}
                            className="mobile-prompt-text-input"
                        />
                    )}
                    <div className="mobile-prompt-options-scroll">
                        <div className="mobile-prompt-options-list">
                            {(activeField.options || []).map((option) => (
                                <div
                                    key={option}
                                    className={`mobile-prompt-option-item ${editedValues[activeField.id] === option ? 'selected' : ''}`}
                                    onClick={() => handleValueChange({ target: { name: activeField.id, value: option } })}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        // Default text input
        return (
            <div className="mobile-prompt-text-content">
                <input
                    type="text"
                    name={activeField.id}
                    value={editedValues[activeField.id] || ''}
                    onChange={handleValueChange}
                    placeholder={activeField.placeholder || 'Enter value...'}
                    className="mobile-prompt-text-input"
                />
            </div>
        );
    };

    return (
        <>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={showConfirmDialog}
                onConfirm={handleConfirmGenerate}
                onCancel={handleCancelGenerate}
                title="Confirm Generation"
                message="Are you sure you want to regenerate designs with these settings?"
            />

            {/* Overlay */}
            {isExpanded && (
                <div className="mobile-prompt-overlay" onClick={handleOverlayClick} />
            )}

            {/* Main Container */}
            <div
                className={`mobile-prompt-container ${isExpanded ? 'expanded' : ''}`}
                ref={panelRef}
            >
                {/* Collapsed State - Icon Bar */}
                {!isExpanded && (
                    <div className="mobile-prompt-collapsed" onClick={handleBarClick}>
                        <div className="mobile-prompt-icons-row">
                            {allFields.map((field) => {
                                const hasValue = field.id === 'assets'
                                    ? editedValues.assets?.length > 0
                                    : !!editedValues[field.id];

                                return (
                                    <div
                                        key={field.id}
                                        className={`mobile-prompt-icon-item ${hasValue ? 'has-value' : ''}`}
                                    >
                                        <img
                                            src={getIcon(field.id, false)}
                                            alt={field.display}
                                            className="mobile-prompt-icon"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <button className="mobile-prompt-submit-btn-collapsed">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M12 4L12 20M12 4L6 10M12 4L18 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Expanded State */}
                {isExpanded && (
                    <div className="mobile-prompt-expanded">
                        {/* Submit Button */}
                        <button className="mobile-prompt-submit-btn" onClick={handleSubmit}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M12 4L12 20M12 4L6 10M12 4L18 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* Icon Row */}
                        <div className="mobile-prompt-icons-expanded">
                            {allFields.map((field) => {
                                const isActive = activeFieldId === field.id;
                                const hasValue = field.id === 'assets'
                                    ? editedValues.assets?.length > 0
                                    : !!editedValues[field.id];

                                return (
                                    <div
                                        key={field.id}
                                        className={`mobile-prompt-icon-item ${isActive ? 'active' : ''} ${hasValue ? 'has-value' : ''}`}
                                        onClick={() => handleFieldSelect(field.id)}
                                    >
                                        <img
                                            src={getIcon(field.id, isActive)}
                                            alt={field.display}
                                            className="mobile-prompt-icon"
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Field Header */}
                        {activeField && (
                            <div className="mobile-prompt-field-header">
                                <span className="mobile-prompt-field-name">
                                    {activeField.display?.toUpperCase() || activeField.id.toUpperCase()}
                                </span>
                                {hasFieldModified(activeFieldId) && (
                                    <button
                                        className="mobile-prompt-confirm-btn"
                                        onClick={handleConfirmField}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Field Content */}
                        <div className="mobile-prompt-field-content">
                            {renderFieldContent()}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MobilePromptBar;

